import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  getAidModel,
  createAidModel,
  updateAidModel,
  getAidModelDataset,
  createAidModelDataset,
  getAidSourceTables,
} from 'api/aid-models/aid-models.api';

import { aidModelsKeys } from '../../hooks/use-aid-models';
import { aidCoaKeys } from './use-aid-coa';

type ClientId = string | number;
type ModelId = string | number;

interface UseAidModelParams {
  userId: string;
  clientId: ClientId;
  modelId: ModelId;
  enabled?: boolean;
}

interface CreateAidModelVariables {
  userId: string;
  clientId: ClientId;
  data: unknown;
}

interface UpdateAidModelVariables {
  userId: string;
  clientId: ClientId;
  modelId: ModelId;
  data: unknown;
}

interface UseAidModelDatasetParams {
  userId: string;
  modelId: ModelId;
  enabled?: boolean;
  refetchInterval?: number | false;
}

interface CreateAidModelDatasetVariables {
  userId: string;
  modelId: ModelId;
  sourceTableName: string;
}

interface UseAidSourceTablesParams {
  userId: string;
  enabled?: boolean;
}

const AID_MODEL_DATASET_KEY = 'aidModelDataset';
const AID_SOURCE_TABLES_KEY = 'aidSourceTables';

export const useAidModel = ({
  userId,
  clientId,
  modelId,
  enabled = true,
}: UseAidModelParams) =>
  useQuery({
    queryKey: aidModelsKeys.detail(clientId, modelId),
    queryFn: () => getAidModel(userId, clientId, modelId),
    enabled: enabled && !!userId && !!clientId && !!modelId,
  });

export const useCreateAidModel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, clientId, data }: CreateAidModelVariables) =>
      createAidModel(userId, clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: aidModelsKeys.lists(),
      });
    },
  });
};

export const useUpdateAidModel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      clientId,
      modelId,
      data,
    }: UpdateAidModelVariables) =>
      updateAidModel(userId, clientId, modelId, data),
    onSuccess: (_, { clientId, modelId }) => {
      queryClient.invalidateQueries({
        queryKey: aidModelsKeys.detail(clientId, modelId),
      });
      queryClient.invalidateQueries({
        queryKey: aidModelsKeys.lists(),
      });
    },
  });
};

export const useAidModelDataset = ({
  userId,
  modelId,
  enabled = true,
  refetchInterval,
}: UseAidModelDatasetParams) =>
  useQuery({
    queryKey: [AID_MODEL_DATASET_KEY, userId, modelId],
    queryFn: () => getAidModelDataset(userId, modelId),
    enabled: enabled && !!userId && !!modelId,
    retry: false,
    refetchInterval,
  });

export const useCreateAidModelDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      modelId,
      sourceTableName,
    }: CreateAidModelDatasetVariables) =>
      createAidModelDataset(userId, modelId, sourceTableName),
    onSuccess: (_, { userId, modelId }) => {
      queryClient.invalidateQueries({
        queryKey: [AID_MODEL_DATASET_KEY, userId, modelId],
      });
      queryClient.invalidateQueries({
        queryKey: aidCoaKeys.model(modelId),
      });
    },
  });
};

export const useAidSourceTables = ({
  userId,
  enabled = true,
}: UseAidSourceTablesParams) =>
  useQuery({
    queryKey: [AID_SOURCE_TABLES_KEY, userId],
    queryFn: () => getAidSourceTables(userId),
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000,
  });
